import ky from 'ky';
import { useAuthStore } from './store';

const API_BASE = 'https://clipgov-api.dicksonmushif-24d.workers.dev';

export const api = ky.create({
  prefixUrl: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
  hooks: {
    beforeRequest: [
      (request) => {
        const token = useAuthStore.getState().token;
        if (token) {
          request.headers.set('Authorization', `Bearer ${token}`);
        }
      },
    ],
  },
});

export interface LoginResponse {
  token: string;
  user: {
    id: string;
    email: string;
    role: 'admin' | 'editor' | 'viewer';
    name: string;
  };
  subscription?: {
    active: boolean;
    expiry?: number;
  };
}

export interface SubscriptionResponse {
  active: boolean;
  plan?: string;
  expiry?: number;
  clips_remaining?: number;
}

export interface Clip {
  id: string;
  title: string;
  start_time: number;
  end_time: number;
  duration: number;
  transcript: string;
  virality_score: number;
  sentiment?: 'positive' | 'negative' | 'neutral';
  caption?: string;
  pa_approved: number;
  created_at: number;
}

export async function login(email: string, password: string, deviceId: string): Promise<LoginResponse> {
  return api.post('api/auth/login', {
    json: { email, password, deviceId },
  }).json();
}

export async function signup(
  email: string, 
  password: string, 
  name: string,
  phone: string,
  deviceId: string
): Promise<{ success: boolean; userId: string; requiresPayment: boolean; message: string }> {
  return api.post('api/auth/signup', {
    json: { email, password, name, phone, deviceId },
  }).json();
}

export async function checkSubscription(userId: string): Promise<SubscriptionResponse> {
  return api.post('api/subscription/check', {
    json: { userId },
  }).json();
}

export async function listClips(userId: string): Promise<{ clips: Clip[] }> {
  return api.get('api/clips', {
    searchParams: { userId },
  }).json();
}

export async function approveClip(clipId: string, approvedBy: string): Promise<{ success: boolean }> {
  return api.post(`api/clip/${clipId}/approve`, {
    json: { approved_by: approvedBy },
  }).json();
}

export async function verifyPayment(
  userId: string,
  transactionId: string,
  amount: number,
  method: 'mpesa' | 'tigopesa' | 'card'
): Promise<{ success: boolean; expiry: number; message: string }> {
  return api.post('api/payment/verify', {
    json: { userId, transactionId, amount, method },
  }).json();
}

export async function adminGetUsers(): Promise<{ users: any[] }> {
  return api.get('api/admin/users').json();
}

export async function adminUpdateUser(
  userId: string,
  updates: { is_active?: boolean; role?: string }
): Promise<{ success: boolean }> {
  return api.put(`api/admin/users/${userId}`, {
    json: updates,
  }).json();
}

export async function adminGetStats(): Promise<{
  totalUsers: number;
  activeUsers: number;
  todayRevenue: number;
  totalRevenue: number;
}> {
  return api.get('api/admin/stats').json();
}
